import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

export interface ExitSlipDoneCountState {
  // How many exit tickets the student has passed — two thirds of the points or more, which is what the
  // unit header's badge means by "done" — keyed by the unit (target) the ticket belongs to. The badge
  // reports the unit on screen alone, like the Support and Target badges beside it. Filled in by
  // DashboardExitSlipQuery.
  //
  // A mapping rather than the single count for the selected unit so that switching units is a pure
  // selector read: the query component would otherwise have to re-derive and re-dispatch, leaving the
  // badge a commit behind the Support and Target counts that move immediately.
  //
  // Redux rather than a prop because the query and the badge sit in sibling subtrees: the query lives
  // under DashboardCards and the header under DashboardContainerBody, with no common parent below the
  // page itself.
  byTarget: Record<string, number>;
}

const initialState: ExitSlipDoneCountState = { byTarget: {} };

export const exitSlipDoneCountSlice = createSlice({
  name: 'exitSlipDoneCount',
  initialState,
  reducers: {
    setExitSlipDoneCountByTarget: (state: ExitSlipDoneCountState, action: PayloadAction<Record<string, number>>) => {
      state.byTarget = action.payload;
    },
  },
});

// A unit with no passed tickets is simply absent from the mapping, so it reads 0. Returns the number
// itself rather than the mapping: useSelector compares by reference, and handing back the object would
// re-render the badge on every unrelated action.
export const selectExitSlipDoneCountForTarget =
  (targetId: string | undefined) =>
  (state: RootState): number =>
    targetId ? state.exitSlipDoneCount.byTarget[targetId] ?? 0 : 0;

export const { setExitSlipDoneCountByTarget } = exitSlipDoneCountSlice.actions;

export default exitSlipDoneCountSlice.reducer;
